import { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import { styled } from "@mui/system";
import { Box, Typography, TextField, Button } from "@mui/material";
import * as allauth from "../../lib/allauth";
import FormErrors from "../../components/FormErrors";
import { useConfig } from "../../auth/hooks";
import { useNavigate } from "react-router-dom";
import PopoverCard from "../../components/PopoverCard";
import BackgroundContainer from '../../components/BackgroundContainer';
import BaseHeader from '../../components/BaseHeader';
import BaseButton from '../../components/BaseButton';
import BaseTextField from '../../components/BaseTextField';
import BaseFormContainer from '../../components/BaseFormContainer';

const Subtitle = styled(Typography)({
  fontFamily: 'Playfair Display, serif',
  fontSize: '1.5rem',
  color: '#ffbfe1',
  textAlign: 'center',
  textShadow: '0px 2px 6px rgba(0, 0, 0, 0.5)',
  lineHeight: '1.4',
});

export default function UpdateEmail() {
  const config = useConfig();
  const [currentEmail, setCurrentEmail] = useState("");
  const [currentEmailVerified, setCurrentEmailVerified] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [redirectToVerification, setRedirectToVerification] = useState(false);
  const navigate = useNavigate();
  const [response, setResponse] = useState({
    fetching: false,
    content: { status: 200, data: [] },
  });

  // Popover state
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [popoverMessage, setPopoverMessage] = useState("");

  useEffect(() => {
    setResponse((r) => ({ ...r, fetching: true }));
    allauth
      .getEmailAddresses()
      .then((resp) => {
        if (resp.status === 200 && resp.data.length > 0) {
          const primaryEmail = resp.data.find((e) => e.primary);
          setCurrentEmail(primaryEmail?.email || "");
          setCurrentEmailVerified(primaryEmail?.verified || false);
        }
      })
      .catch((e) => {
        console.error(e);
        setPopoverMessage(e.message);
        setPopoverOpen(true);
      })
      .finally(() => setResponse((r) => ({ ...r, fetching: false })));
  }, []);

  function updateEmail() {
    setResponse({ ...response, fetching: true });

    if (!newEmail) {
      setPopoverMessage("Please enter a new email address.");
      setPopoverOpen(true);
      setResponse((r) => ({ ...r, fetching: false }));
      return;
    }

    allauth
      .addEmail(newEmail)
      .then((resp) => {
        if (resp.status === 200) {
          return allauth.markEmailAsPrimary(newEmail);
        } else {
          throw new Error("Failed to add new email.");
        }
      })
      .then((resp) => {
        if (resp.status === 200) {
          return allauth.deleteEmail(currentEmail);
        } else {
          // If we failed to mark as primary, revert the new email
          allauth.deleteEmail(newEmail);
          throw new Error(
            "Make sure before you change email, you verify your current one."
          );
        }
      })
      .then((resp) => {
        if (resp.status === 200) {
          if (config.data.account.email_verification_by_code_enabled) {
            setRedirectToVerification(true);
          } else {
            setPopoverMessage("Email updated successfully.");
            setPopoverOpen(true);
            setCurrentEmail(newEmail);
            setCurrentEmailVerified(false);
            setNewEmail("");
          }
        } else {
          throw new Error("Failed to delete old email.");
        }
      })
      .catch((e) => {
        console.error(e);
        setPopoverMessage(e.message);
        setPopoverOpen(true);
      })
      .finally(() => {
        setResponse((r) => ({ ...r, fetching: false }));
      });
  }

  function resendVerificationEmail() {
    setResponse({ ...response, fetching: true });
    allauth
      .requestEmailVerification(currentEmail)
      .then((resp) => {
        if (resp.status === 200) {
          if (config.data.account.email_verification_by_code_enabled) {
            setRedirectToVerification(true);
          } else {
            setPopoverMessage("Verification email sent! Check your inbox.");
            setPopoverOpen(true);
          }
        } else {
          throw new Error("Failed to send verification email.");
        }
      })
      .catch((e) => {
        console.error(e);
        setPopoverMessage(e.message);
        setPopoverOpen(true);
      })
      .finally(() => {
        setResponse((r) => ({ ...r, fetching: false }));
      });
  }

  if (redirectToVerification) {
    return <Navigate to="/account/verify-email" />;
  }

  return (
    <BackgroundContainer>
      <BaseFormContainer>
      <BaseHeader sx={{
  fontSize: '3rem',
  '@media (max-width: 380px)': { fontSize: '2.5rem' },
  '@media (max-width: 320px)': { fontSize: '2rem' },
  paddingTop: '10px',
  marginBottom: '0.5rem',
  textAlign: 'center',
}}>Update Email</BaseHeader>
      <FormErrors errors={response.content.errors} />

      <Box display="flex" flexDirection="column" alignItems="center">
        <Subtitle>
          <strong>Current Email:</strong> {currentEmail || "Loading..."}
        </Subtitle>
        <Subtitle
          style={{
            color: currentEmailVerified ? "green" : "red", // Green if verified, red if not
            fontSize: '1.2rem',
            fontWeight: "bold",
            marginTop:'5px'
          }}
        >
          {currentEmailVerified ? "✅ Email Verified" : "❌ Email Not Verified"}
        </Subtitle>
        {!currentEmailVerified && (
          <BaseButton sx={{ marginBottom: '20px', width: 'auto', padding: '8px 20px', fontSize: '0.85rem' }} disabled={response.fetching} onClick={resendVerificationEmail}>
            Resend Verification Email
          </BaseButton>
        )}
        {!currentEmailVerified && (
          <Subtitle sx = {{marginBottom : '10px'}} >
            <strong>Not your Email?</strong>
          </Subtitle>
        )}
        {!currentEmailVerified && (
          <BaseTextField
            label="New Email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            type="email"
            required
            placeholder="Enter your new email"
          />
        )}
        {!currentEmailVerified && (
          <BaseButton disabled={response.fetching} onClick={updateEmail}>
            Update Email
          </BaseButton>
        )}
        <BaseButton sx={{marginTop: '20px'}} onClick={() => navigate("/")}>Back</BaseButton>
      </Box>

      {/* Popover for messages (in place of window.alert) */}
      <PopoverCard
        mainText={popoverMessage}
        open={popoverOpen}
        onClose={() => setPopoverOpen(false)}
        showButtonOne={false}
        showButtonTwo={false}
        textButtonOne=""
        textButtonTwo=""
      />
      </BaseFormContainer>
    </BackgroundContainer>
  );
}
